import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useTypedSelector } from '../store/reducers/use-typed-selector';
import { getPasscodeLists } from '../store/actions/admin/passcode-lists';
import { updateRegistrationSteps, updateRequiredQuestions } from '../store/actions/admin/create-event';
import { GateTypes, PASSCODE_LIST_TYPE, RegistrationTypes, RegFieldsEnum } from '../types/working-model';


export const useFieldRequired = (): RegFieldsEnum[] => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const passcodeLists = useTypedSelector(state => state.PasscodeListReducer.passcodeLists);

	const dispatch = useDispatch();

	if (!workingEvent) return [];

	useEffect(() => {
		dispatch(getPasscodeLists(user!.active_channel, token!));
	}, [dispatch, token, user]);

	const required: RegFieldsEnum[] = [];
	const gating = workingEvent.registration_settings?.type === RegistrationTypes.gated;

	const findPasscodeLists = (listTypesArray: PASSCODE_LIST_TYPE[]): boolean => {
		const passcodeListObjects = workingEvent.registration_settings?.requirements?.[GateTypes.passcode_list];
		const passcodeListMatch = passcodeListObjects?.find(listObject => listObject?.passcodeList?.find(list => {
			const listType = passcodeLists.find(passcodeList => passcodeList.passcode_list === list)?.type;
			if (listType !== undefined && listTypesArray.includes(listType)) return true;
		}));
		return passcodeListMatch ? true : false;
	};

	// email must be turned on and required if ANY of the following are being used:
	// email confirmation, profile, ticketing, passcode list using email, domains specifically allowed or blocked
	// TODO: update to add Breakout Rooms, Firesides, and Networking
	const emailConfirm = workingEvent.registration_settings?.registrationConfirmation;
	const profile = workingEvent.registration_steps && workingEvent.registration_steps.find(step => step.type === "profile")?.isOn;
	const ticketing = workingEvent.registration_steps && workingEvent.registration_steps.find(step => step.type === "ticketing")?.isOn;
	// but only check passcodes and domains if registration gating is turned on
	const emailPasscodeList = gating && findPasscodeLists([PASSCODE_LIST_TYPE.EMAILS_ONLY, PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES]);
	const allowedDomains = gating && workingEvent.registration_settings?.requirements?.[GateTypes.domain_email];
	const blockedDomains = gating && workingEvent.registration_settings?.useBlockedDomainEmails;

	if (emailConfirm || profile || ticketing || emailPasscodeList || allowedDomains || blockedDomains) {
		required.push(RegFieldsEnum.email);
	}

	// passcode must be turned on and required if ANY of the following are being used:
	// shared passcode, passcode lists other than email only (but only if registration gating is currently on)
	// TODO: maybe future requirements related to breakout rooms, networking, etc
	const passcode = gating && workingEvent.registration_settings?.requirements[GateTypes.shared_passcode];
	const passcodeList = gating && findPasscodeLists([PASSCODE_LIST_TYPE.PASSCODES_ONLY, PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES]);

	if (passcode || passcodeList) {
		required.push(RegFieldsEnum.passcode);
	}

	// compiled array of registration fields required for other registration steps currently in use
	return required;
};

export const useSetRequiredField = async (required: RegFieldsEnum[]): Promise<void> => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const registrationQuestions = useTypedSelector(state => state.RegistrationQuestionsReducer.registrationQuestions);
	const dispatch = useDispatch();

	useEffect(() => {
		if (!workingEvent?.registration_steps) return;

		const steps = workingEvent.registration_steps;
		let generalStepQuestions = steps[0].questions;
		let reqQuestions = workingEvent.required_registration_questions || [];
		// track if steps or requiredQuestions change and need to be updated
		let updatedSteps = false;
		let updatedReqQuestions = false;

		required.forEach(fieldID => {
			// field must be included in the general registration questions array
			const fieldInList = generalStepQuestions && generalStepQuestions.find(question => question.registration_question === fieldID);
			if (!fieldInList) {
				updatedSteps = true;
				const regQuestion = registrationQuestions.find(question => question.registration_question === fieldID);
				if (regQuestion) {
					generalStepQuestions ? generalStepQuestions.push(regQuestion) : generalStepQuestions = [regQuestion];
				}
			}
			// field must be included in the array of required questions
			const fieldRequired = reqQuestions && reqQuestions.includes(fieldID);
			if (!fieldRequired) {
				updatedReqQuestions = true;
				reqQuestions ? reqQuestions.push(fieldID) : reqQuestions = [fieldID];
			}
		});

		// only dispatch if changes have been made
		if (updatedSteps) {
			dispatch(updateRegistrationSteps([...steps,]));
			updatedSteps = false;
		}
		if (updatedReqQuestions) {
			dispatch(updateRequiredQuestions(reqQuestions));
			updatedReqQuestions = false;
		}
	}, [required, dispatch, workingEvent, registrationQuestions]);
};